import { useState, useRef } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  RiImageLine,
  RiLink,
  RiTextWrap,
  RiShareLine,
  RiDeleteBin6Line,
  RiDragMove2Line,
  RiAddLine,
  RiSettings3Line,
  RiEyeLine,
} from "react-icons/ri";

// Draggable component
const DraggableComponent = ({ component }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: "component",
    item: { id: component.id },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag}
      className={`flex items-center p-3 bg-white rounded-lg shadow-sm border border-secondary-200 cursor-move hover:border-primary-500 hover:shadow-md transition-all ${
        isDragging ? "opacity-50" : ""
      }`}
    >
      <component.icon className="w-5 h-5 mr-2 text-primary-500" />
      {component.name}
    </div>
  );
};

// Section component
const Section = ({ section, index, onUpdate, onDelete, onMove }) => {
  const [showSettings, setShowSettings] = useState(false);

  const ref = useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: "section",
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: "section",
    hover(item, monitor) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      onMove(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  drag(drop(ref));

  const renderContent = () => {
    switch (section.type) {
      case "text":
        return (
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <select
                className="p-1 text-sm border rounded"
                value={section.variant || "paragraph"}
                onChange={(e) =>
                  onUpdate(index, { ...section, variant: e.target.value })
                }
              >
                <option value="paragraph">Paragraph</option>
                <option value="heading1">Heading 1</option>
                <option value="heading2">Heading 2</option>
              </select>
              {showSettings && (
                <input
                  type="color"
                  value={section.color || "#000000"}
                  onChange={(e) =>
                    onUpdate(index, { ...section, color: e.target.value })
                  }
                  className="w-8 h-8 p-1 rounded"
                />
              )}
            </div>
            <textarea
              className={`w-full p-3 rounded border focus:border-primary-500 focus:ring-1 focus:ring-primary-500 ${
                section.variant === "heading1"
                  ? "text-2xl font-bold"
                  : section.variant === "heading2"
                    ? "text-xl font-semibold"
                    : ""
              }`}
              style={{ color: section.color }}
              value={section.content}
              onChange={(e) =>
                onUpdate(index, { ...section, content: e.target.value })
              }
              placeholder="Enter your text here..."
              rows={section.variant === "paragraph" ? 4 : 2}
            />
          </div>
        );

      case "image":
        return (
          <div className="space-y-3">
            <div className="flex space-x-2">
              <input
                type="text"
                className="flex-1 p-3 rounded border focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                value={section.content}
                onChange={(e) =>
                  onUpdate(index, { ...section, content: e.target.value })
                }
                placeholder="Enter image URL"
              />
              <button className="px-4 py-2 bg-secondary-100 rounded hover:bg-secondary-200">
                Upload
              </button>
            </div>
            {section.content && (
              <div className="relative group">
                <img
                  src={section.content}
                  alt="Preview"
                  className="max-w-full rounded shadow-sm"
                />
                {showSettings && (
                  <div className="absolute bottom-2 right-2 bg-white rounded-lg shadow-lg p-2 space-y-2">
                    <input
                      type="range"
                      min="200"
                      max="800"
                      value={section.width || 400}
                      onChange={(e) =>
                        onUpdate(index, {
                          ...section,
                          width: parseInt(e.target.value),
                        })
                      }
                      className="w-32"
                    />
                    <select
                      className="w-full p-1 text-sm border rounded"
                      value={section.alignment || "center"}
                      onChange={(e) =>
                        onUpdate(index, {
                          ...section,
                          alignment: e.target.value,
                        })
                      }
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case "button":
        return (
          <div className="space-y-3">
            <input
              type="text"
              className="w-full p-3 rounded border focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              value={section.content}
              onChange={(e) =>
                onUpdate(index, { ...section, content: e.target.value })
              }
              placeholder="Button text"
            />
            {showSettings && (
              <div className="flex space-x-2">
                <input
                  type="text"
                  className="flex-1 p-2 text-sm border rounded"
                  value={section.url || ""}
                  onChange={(e) =>
                    onUpdate(index, { ...section, url: e.target.value })
                  }
                  placeholder="Button URL"
                />
                <select
                  className="p-2 text-sm border rounded"
                  value={section.style || "primary"}
                  onChange={(e) =>
                    onUpdate(index, { ...section, style: e.target.value })
                  }
                >
                  <option value="primary">Primary</option>
                  <option value="secondary">Secondary</option>
                  <option value="outline">Outline</option>
                </select>
              </div>
            )}
            <button
              className={`px-6 py-3 rounded-lg font-medium ${
                section.style === "secondary"
                  ? "bg-secondary-500 text-white"
                  : section.style === "outline"
                    ? "border-2 border-primary-500 text-primary-500"
                    : "bg-primary-500 text-white"
              }`}
            >
              {section.content || "Button"}
            </button>
          </div>
        );

      case "social":
        return (
          <div className="space-y-3">
            {showSettings && (
              <div className="grid grid-cols-2 gap-2">
                {["facebook", "twitter", "linkedin", "instagram"].map(
                  (platform) => (
                    <div key={platform} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={
                          section.platforms
                            ? section.platforms.includes(platform)
                            : true
                        }
                        onChange={(e) => {
                          const platforms = section.platforms || [
                            "facebook",
                            "twitter",
                            "linkedin",
                            "instagram",
                          ];
                          if (e.target.checked) {
                            platforms.push(platform);
                          } else {
                            platforms.splice(platforms.indexOf(platform), 1);
                          }
                          onUpdate(index, { ...section, platforms });
                        }}
                      />
                      <label className="capitalize">{platform}</label>
                    </div>
                  ),
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {(
                section.platforms || [
                  "facebook",
                  "twitter",
                  "linkedin",
                  "instagram",
                ]
              ).map((platform) => (
                <button
                  key={platform}
                  className="px-4 py-2 rounded-lg font-medium bg-secondary-100 hover:bg-secondary-200 transition-colors capitalize"
                >
                  {platform}
                </button>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      ref={ref}
      className={`p-4 bg-white rounded-lg shadow-sm border border-secondary-200 relative group ${
        isDragging ? "opacity-50" : ""
      }`}
    >
      <div className="absolute top-2 right-2 flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="p-1.5 text-secondary-500 hover:bg-secondary-100 rounded"
        >
          <RiSettings3Line className="w-5 h-5" />
        </button>
        <button
          onClick={() => onDelete(index)}
          className="p-1.5 text-red-500 hover:bg-red-50 rounded"
        >
          <RiDeleteBin6Line className="w-5 h-5" />
        </button>
        <div className="p-1.5 text-secondary-400 cursor-move">
          <RiDragMove2Line className="w-5 h-5" />
        </div>
      </div>
      {renderContent()}
    </div>
  );
};

// Drop zone
const DropZone = ({ onDrop, children }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: "component",
    drop: (item) => onDrop(item),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  return (
    <div
      ref={drop}
      className={`min-h-[300px] bg-white rounded-lg border-2 border-dashed transition-colors ${
        isOver ? "border-primary-500 bg-primary-50" : "border-secondary-300"
      } p-4`}
    >
      {children}
    </div>
  );
};

// Preview component
const EmailPreview = ({ sections }) => {
  const renderSection = (section) => {
    switch (section.type) {
      case "text":
        return (
          <div
            className={`${
              section.variant === "heading1"
                ? "text-2xl font-bold"
                : section.variant === "heading2"
                  ? "text-xl font-semibold"
                  : ""
            }`}
            style={{ color: section.color }}
          >
            {section.content || "Text content"}
          </div>
        );
      case "image":
        return section.content ? (
          <div
            style={{
              textAlign: section.alignment || "center",
              maxWidth: section.width || 400,
              margin: "0 auto",
            }}
          >
            <img
              src={section.content}
              alt="Preview"
              className="rounded"
              style={{ width: "100%" }}
            />
          </div>
        ) : (
          <div className="bg-secondary-100 h-32 rounded flex items-center justify-center text-secondary-400">
            Image placeholder
          </div>
        );
      case "button":
        return (
          <div className="text-center">
            <button
              className={`px-6 py-3 rounded-lg font-medium ${
                section.style === "secondary"
                  ? "bg-secondary-500 text-white"
                  : section.style === "outline"
                    ? "border-2 border-primary-500 text-primary-500"
                    : "bg-primary-500 text-white"
              }`}
            >
              {section.content || "Button"}
            </button>
          </div>
        );
      case "social":
        return (
          <div className="flex justify-center gap-2">
            {(section.platforms || ["facebook", "twitter", "linkedin"]).map(
              (platform) => (
                <button
                  key={platform}
                  className="px-4 py-2 rounded-lg font-medium bg-secondary-100 capitalize"
                >
                  {platform}
                </button>
              ),
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6 space-y-6">
      {sections.map((section, index) => (
        <div key={section.id}>{renderSection(section)}</div>
      ))}
    </div>
  );
};

const EmailEditor = ({ value, onChange, variables }) => {
  const [sections, setSections] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  const components = [
    { id: "text", name: "Text Block", icon: RiTextWrap },
    { id: "image", name: "Image", icon: RiImageLine },
    { id: "button", name: "Button", icon: RiLink },
    { id: "social", name: "Social Links", icon: RiShareLine },
  ];

  const handleDrop = (item) => {
    const newSections = [
      ...sections,
      {
        type: item.id,
        content: "",
        id: Date.now(),
      },
    ];
    setSections(newSections);
    updateParent(newSections);
  };

  const handleSectionUpdate = (index, updatedSection) => {
    const newSections = [...sections];
    newSections[index] = updatedSection;
    setSections(newSections);
    updateParent(newSections);
  };

  const handleSectionDelete = (index) => {
    const newSections = sections.filter((_, i) => i !== index);
    setSections(newSections);
    updateParent(newSections);
  };

  const updateParent = (newSections) => {
    const content = newSections
      .map((section) => {
        switch (section.type) {
          case "text":
            return section.content;
          case "image":
            return `[Image: ${section.content}]`;
          case "button":
            return `[Button: ${section.content}]`;
          case "social":
            return `[Social Links]`;
          default:
            return "";
        }
      })
      .join("\n\n");
    onChange(content);
  };

  const moveSection = (dragIndex, hoverIndex) => {
    const newSections = [...sections];
    const dragSection = newSections[dragIndex];
    newSections.splice(dragIndex, 1);
    newSections.splice(hoverIndex, 0, dragSection);
    setSections(newSections);
    updateParent(newSections);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="space-y-4">
        <div className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm border border-secondary-200">
          <div className="flex space-x-4">
            {components.map((component) => (
              <DraggableComponent key={component.id} component={component} />
            ))}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                showPreview
                  ? "bg-primary-500 text-white"
                  : "text-primary-500 hover:bg-primary-50"
              }`}
            >
              <RiEyeLine className="w-5 h-5 mr-2" />
              Preview
            </button>
            <button className="flex items-center px-4 py-2 text-primary-500 hover:bg-primary-50 rounded-lg">
              <RiAddLine className="w-5 h-5 mr-2" />
              Save Template
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-4">
            <DropZone onDrop={handleDrop}>
              {sections.length === 0 ? (
                <div className="text-center text-secondary-400 py-12">
                  <RiDragMove2Line className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Drag and drop components here to build your email</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {sections.map((section, index) => (
                    <Section
                      key={section.id}
                      section={section}
                      index={index}
                      onUpdate={handleSectionUpdate}
                      onDelete={handleSectionDelete}
                      onMove={moveSection}
                    />
                  ))}
                </div>
              )}
            </DropZone>
          </div>

          {showPreview && (
            <div className="lg:sticky lg:top-4">
              <EmailPreview sections={sections} />
            </div>
          )}
        </div>
      </div>
    </DndProvider>
  );
};

export default EmailEditor;
